/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ImageUrlGenerator } from "@tivocorp/data-domain-lib";
import type { Carousel, Carousels, FeedItem } from "@tivocorp/data-domain-lib";

import { CarouselTemplate, OfferType } from "../../model/enums";
import { getTilePixelDimensions } from "../utility/CarouselTemplateUtil";
import type {
  TCarouselItem,
  TCarouselRail,
  TContentType
} from "../../model/types";
import type {
  TCarouselRailPage,
  TTilePage,
  TTilePaginationState
} from "./homescreenTypes";

const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();

/**
 * Helper to safely extract resolvedCount from a FeedItems-like object.
 * Returns the resolvedCount if present and valid, otherwise falls back to the provided default.
 */
const getResolvedCountFromFeedItems = (
  fi: { feedItems?: FeedItem[]; resolvedCount?: number } | undefined | null,
  fallback: number
): number => {
  if (fi !== undefined && fi !== null) {
    const count = fi.resolvedCount;
    if (count !== undefined && count !== null) {
      return count;
    }
  }
  return fallback;
};

/**
 * Maps a domain-lib FeedItemType string to the app TContentType.
 */
const mapFeedItemTypeToContentType = (feedItemType: string): TContentType => {
  switch (feedItemType) {
    case "Episode":
      return "episode";
    case "Movie":
      return "movie";
    case "Series":
      return "series";
    case "SportsEvent":
      return "sports";
    case "SportsOrganization":
      return "leagueOrganization";
    case "SportsTeam":
      return "team";
    case "JumpChannel":
    case "LinearProvider":
      return "channel";
    case "StreamingProvider":
      return "ottApplication";
    default:
      return "channel";
  }
};

/**
 * Maps a single domain-lib FeedItem to the app TCarouselItem.
 */
const mapFeedItemToCarouselItem = (
  feedItem: FeedItem,
  imageHostBaseUrl: string,
  carouselTemplate?: CarouselTemplate
): TCarouselItem => {
  const isAdult: boolean =
    feedItem.isAdult !== undefined && feedItem.isAdult !== null
      ? feedItem.isAdult
      : false;
  const isLive: boolean =
    feedItem.isLive !== undefined && feedItem.isLive !== null
      ? feedItem.isLive
      : false;
  const duration: number =
    feedItem.durationSecs !== undefined && feedItem.durationSecs !== null
      ? feedItem.durationSecs
      : 0;

  const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(
    feedItem.feedItemType
  );

  const resolvedTemplate: CarouselTemplate =
    carouselTemplate !== undefined && carouselTemplate !== null
      ? carouselTemplate
      : CarouselTemplate.LANDSCAPE_HORIZONTAL;
  const tileDimensions = getTilePixelDimensions(resolvedTemplate);
  const tileWidth = tileDimensions.width;
  const tileHeight = tileDimensions.height;

  const imageUrl: string =
    imageHostBaseUrl.length > 0
      ? imageUrlGenerator.buildImageUrl(
          imageHostBaseUrl,
          feedItem.digItemId || "",
          imageType,
          tileWidth,
          tileHeight
        )
      : "";

  const item: TCarouselItem = {
    id: feedItem.feedItemId,
    feedItemType: feedItem.feedItemType,
    title: feedItem.title,
    description: feedItem.caption !== undefined ? feedItem.caption : "",
    imageUrl: imageUrl,
    contentType: mapFeedItemTypeToContentType(feedItem.feedItemType),
    categoryLabel: feedItem.caption !== undefined ? feedItem.caption : "",
    duration: duration,
    isLive: isLive,
    isUpcoming: feedItem.isUpcoming,
    isAdult: isAdult,
    offerType: feedItem.offerType as OfferType | undefined,
    startTime: feedItem.startTime,
    endTime: feedItem.endTime,
    stationId: feedItem.stationId,
    seasonNumber: feedItem.seasonNum,
    episodeNumber: feedItem.episodeNum,
    episodeTitle: feedItem.eventTitle,
    ratingBadge: feedItem.tivoTvRating,
    isNew: feedItem.isNew,
    progressPercent: feedItem.percentWatched,
    movieYear: feedItem.movieYear,
    offerId: feedItem.offerId
  };

  return item;
};

const KNOWN_CAROUSEL_TEMPLATES: Set<string> = new Set(
  Object.values(CarouselTemplate)
);

/**
 * Resolves a raw template string from the service to a known CarouselTemplate.
 * Falls back to LANDSCAPE_HORIZONTAL for any unrecognised or missing value.
 */
const resolveCarouselTemplate = (
  raw: string | undefined | null
): CarouselTemplate => {
  if (raw !== undefined && raw !== null && KNOWN_CAROUSEL_TEMPLATES.has(raw)) {
    return raw as CarouselTemplate;
  }
  return CarouselTemplate.LANDSCAPE_HORIZONTAL;
};

/**
 * Maps a single domain-lib Carousel to the app TCarouselRail.
 */
const mapCarouselToRail = (
  carousel: Carousel,
  imageHostBaseUrl: string
): TCarouselRail => {
  const feedItems: FeedItem[] =
    carousel.feedItems !== undefined &&
    carousel.feedItems !== null &&
    carousel.feedItems.feedItems !== undefined &&
    carousel.feedItems.feedItems !== null
      ? carousel.feedItems.feedItems
      : [];

  const resolvedTemplate: CarouselTemplate = resolveCarouselTemplate(
    carousel.template
  );

  return {
    id: carousel.carouselName,
    title: carousel.caption,
    template: resolvedTemplate,
    items: feedItems.map(
      (feedItem: FeedItem): TCarouselItem =>
        mapFeedItemToCarouselItem(feedItem, imageHostBaseUrl, resolvedTemplate)
    )
  };
};

/**
 * Maps the full domain-lib Carousels response to an array of TCarouselRail.
 */
export const mapCarouselsResponseToRails = (
  response: Carousels,
  imageHostBaseUrl: string
): TCarouselRail[] => {
  const carousels: Carousel[] =
    response.carousels !== undefined && response.carousels !== null
      ? response.carousels
      : [];

  return carousels.map(
    (carousel: Carousel): TCarouselRail =>
      mapCarouselToRail(carousel, imageHostBaseUrl)
  );
};

/**
 * Maps the full domain-lib Carousels response to a cursor-paginated page
 * of rails. Preserves the server-issued pageCursor so the next request
 * can resume from where this one left off.
 */
export const mapCarouselsResponseToPage = (
  response: Carousels,
  imageHostBaseUrl: string
): TCarouselRailPage => {
  const cursor: string | null =
    response.pageCursor !== undefined &&
    response.pageCursor !== null &&
    response.pageCursor.length > 0
      ? response.pageCursor
      : null;
  return {
    items: mapCarouselsResponseToRails(response, imageHostBaseUrl),
    pageCursor: cursor
  };
};

/**
 * Maps a single domain-lib Carousel response (carouselGet) to an
 * offset-paginated page of tiles. The resolvedCount comes from
 * FeedItems.resolvedCount when present and falls back to items.length.
 */
export const mapCarouselResponseToTilePage = (
  response: Carousel,
  imageHostBaseUrl: string
): TTilePage => {
  const fi = response.feedItems;
  const feedItems: FeedItem[] = fi ? fi.feedItems || [] : [];
  const items: TCarouselItem[] = feedItems.map(
    (feedItem: FeedItem): TCarouselItem =>
      mapFeedItemToCarouselItem(
        feedItem,
        imageHostBaseUrl,
        response.template as CarouselTemplate
      )
  );

  return {
    items,
    resolvedCount: getResolvedCountFromFeedItems(fi, items.length)
  };
};

/**
 * Seeds the per-rail offset pagination map from an initial screenGet
 * response. Each rail is keyed by carouselName and starts with the items
 * already returned in the initial response, plus the resolvedCount the
 * server reported (falling back to items.length when absent).
 */
export const mapCarouselsResponseToInitialTilePagination = (
  response: Carousels,
  imageHostBaseUrl: string
): Record<string, TTilePaginationState> => {
  const carousels: Carousel[] = response.carousels || [];

  const seed: Record<string, TTilePaginationState> = {};
  for (const carousel of carousels) {
    const fi = carousel.feedItems;
    const feedItems: FeedItem[] = fi ? fi.feedItems || [] : [];
    const items: TCarouselItem[] = feedItems.map(
      (feedItem: FeedItem): TCarouselItem =>
        mapFeedItemToCarouselItem(
          feedItem,
          imageHostBaseUrl,
          carousel.template as CarouselTemplate
        )
    );

    seed[carousel.carouselName] = {
      items,
      resolvedCount: getResolvedCountFromFeedItems(fi, items.length),
      isLoading: false,
      error: ""
    };
  }
  return seed;
};
